import type { Language } from "../types";

/** 中文仍用 Intl；英文一律用手动格式化，避免中文系统下 en-US 仍被渲染成中文。 */
const ZH_LOCALE = "zh-CN";

const MONTH_LONG_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_SHORT_EN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function toDate(value: Date | number | string): Date {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  return new Date(value);
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** 英文：MM/DD/YYYY */
function formatEnDateNumeric(d: Date): string {
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

function formatEnTime(
  d: Date,
  opts: { seconds: boolean; hour12: boolean },
): string {
  const h24 = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  if (!opts.hour12) {
    return opts.seconds
      ? `${pad2(h24)}:${pad2(m)}:${pad2(s)}`
      : `${pad2(h24)}:${pad2(m)}`;
  }
  const ampm = h24 >= 12 ? "PM" : "AM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return opts.seconds
    ? `${h12}:${pad2(m)}:${pad2(s)} ${ampm}`
    : `${h12}:${pad2(m)} ${ampm}`;
}

/** 供 `<input type="date" lang="…">` 等 */
export function uiHtmlLang(lang: Language): string {
  return lang === "zh" ? "zh-CN" : "en-US";
}

/** 扫描详情顶栏、时间线 */
export function formatUiDateTimeMed(
  value: Date | number | string,
  lang: Language,
): string {
  const d = toDate(value);
  if (lang === "en") {
    const mo = MONTH_SHORT_EN[d.getMonth()];
    return `${mo} ${d.getDate()}, ${d.getFullYear()}, ${formatEnTime(d, { seconds: false, hour12: true })}`;
  }
  return new Intl.DateTimeFormat(ZH_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** 仅日期：对比标题、时间轴 */
export function formatUiDateLong(
  value: Date | number | string,
  lang: Language,
): string {
  const d = toDate(value);
  if (lang === "en") {
    const mo = MONTH_LONG_EN[d.getMonth()];
    return `${mo} ${d.getDate()}, ${d.getFullYear()}`;
  }
  return new Intl.DateTimeFormat(ZH_LOCALE, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

/** 表格一行：含秒 */
export function formatUiDateTimeFull(
  value: Date | number | string,
  lang: Language,
): string {
  const d = toDate(value);
  if (lang === "en") {
    return `${formatEnDateNumeric(d)}, ${formatEnTime(d, { seconds: true, hour12: true })}`;
  }
  return new Intl.DateTimeFormat(ZH_LOCALE, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}

/** 扫描记录表：日期 / 时间两行 */
export function formatUiTableDateTimeParts(
  value: Date | number | string,
  lang: Language,
): { date: string; time: string } {
  const d = toDate(value);
  if (lang === "en") {
    return {
      date: formatEnDateNumeric(d),
      time: formatEnTime(d, { seconds: true, hour12: true }),
    };
  }
  const date = new Intl.DateTimeFormat(ZH_LOCALE, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const time = new Intl.DateTimeFormat(ZH_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
  return { date, time };
}

export function formatUiNumber(value: number, lang: Language): string {
  if (lang === "en") {
    return value.toLocaleString("en-US");
  }
  return value.toLocaleString(ZH_LOCALE);
}
